<template>
  <div
    v-if="data.isDragNDropAreaShowed"
    :class="{
      'drop-overlay': true,
      'drop-overlay--dragover': data.isDragNDropAreaHovered,
    }"
    @drop.prevent="handleDrop($event)"
    @dragover.prevent="data.isDragNDropAreaHovered = true"
    @dragleave.prevent="data.isDragNDropAreaHovered = false"
  >
    <div
      :class="{
        'drop-overlay__icon': true,
        'drop-overlay__icon--small': size === 'small',
        'drop-overlay__icon--medium': size === 'medium',
        'drop-overlay__icon--large': size === 'large',
      }"
      v-html="icon"
    ></div>
    <div
      :class="{
        'drop-overlay__text': true,
        'drop-overlay__text--small': size === 'small',
        'drop-overlay__text--medium': size === 'medium',
        'drop-overlay__text--large': size === 'large',
      }"
    >
      <slot>Drop files to upload</slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import uploadArrowCloudIcon from '@icons/other/upload-arrow-cloud-icon.svg';
import { isVueDraggableMoveEvent } from '@lib/drag-helper';
import { getCurrentInstance, reactive } from 'vue';
import { useDocumentEventListener } from '@lib/events';
import { usePasteUpload } from '@lib/paste-upload';
import { assertUnreachable } from '@lib/type-helpers.ts';

const {
  size = 'medium',
  mode = 'file-upload',
  icon = uploadArrowCloudIcon,
  paste = true,
} = defineProps<{
  size?: 'small' | 'medium' | 'large';
  mode?: 'file-upload' | 'move';
  icon?: string;
  /** Turn off where two droppers share a screen and only one of them should receive a paste. */
  paste?: boolean;
}>();

const emit = defineEmits<{
  'upload-files': [files: FileList | File[], event: DragEvent];
  drop: [event: DragEvent];
}>();

const data = reactive({
  isDragNDropAreaShowed: false,
  isDragNDropAreaHovered: false,
  isDragNDropAreaShowedTimeout: undefined as undefined | ReturnType<typeof setTimeout>,
});

const instance = getCurrentInstance();

// With a modal open, the page behind it must not light up through the backdrop. Anything in the
// overlay layer is exempt: modals, poppers and flyouts all teleport to #portal-target as
// siblings, so a dropper stacked above the modal is never *contained* by it.
function isBehindOpenModal(): boolean {
  const openModals = document.querySelectorAll('.modal-wrap:not(.modal-wrap--do-not-render)');
  if (!openModals.length) return false;

  // The overlay itself is v-if'd away while hidden, so anchor on its parent instead
  const anchor = (instance?.proxy?.$el as Node | undefined)?.parentElement;
  if (!anchor) return false;
  return !anchor.closest('#portal-target');
}

function showDragNDropOverlay(event: DragEvent) {
  if (mode === 'file-upload' && isVueDraggableMoveEvent(event)) {
    // Prevent drags started by vue-draggable from triggering the overlay
    return;
  }

  if (isBehindOpenModal()) return;

  clearTimeout(data.isDragNDropAreaShowedTimeout);
  data.isDragNDropAreaShowed = true;
  data.isDragNDropAreaShowedTimeout = setTimeout(() => (data.isDragNDropAreaShowed = false), 1000);
}

function hideDragNDropOverlay() {
  // All this setTimeout/clearTimeout things is needed to avoid fast toggle of the "this.isDragNDropAreaShowed" data property and Drag&Drop gallery overlay blinking
  clearTimeout(data.isDragNDropAreaShowedTimeout);
  data.isDragNDropAreaShowedTimeout = setTimeout(() => (data.isDragNDropAreaShowed = false), 200);
}

function handleDrop(event: DragEvent) {
  switch (mode) {
    case 'file-upload': {
      if (!event.dataTransfer?.files.length) return;
      const files = event.dataTransfer?.files || (event.target as HTMLInputElement)?.files;
      if (!files || !files.length) return;

      emit('upload-files', files, event);
      break;
    }

    case 'move': {
      emit('drop', event);
      break;
    }

    default:
      assertUnreachable(mode);
  }

  hideDragNDropOverlay();
}

useDocumentEventListener('dragstart', showDragNDropOverlay);
useDocumentEventListener('dragend', hideDragNDropOverlay);
useDocumentEventListener('dragover', showDragNDropOverlay);
useDocumentEventListener('dragleave', hideDragNDropOverlay);
useDocumentEventListener('drop', hideDragNDropOverlay);

// Ctrl-V: feed clipboard files (e.g. a copied screenshot) into this dropper as
// if they were dropped onto it. Only file-upload droppers participate, and the
// shared registry routes the paste to the most recently mounted one.
usePasteUpload(
  (files) => {
    // Some consumers read the files off the (drag) event rather than the first
    // argument, so hand them a minimal event-shaped object carrying the files.
    const syntheticEvent = { dataTransfer: { files }, preventDefault: () => {} } as unknown as DragEvent;
    emit('upload-files', files, syntheticEvent);
  },
  () => mode === 'file-upload' && paste
);
</script>

<style scoped lang="scss">
@import '@styles/variables';

// ============================================================================
// Drag & Drop overlay
.drop-overlay {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-content: center;
  align-items: center;
  font-size: 2rem;
  font-family: 'Montserrat', sans-serif;
  background-color: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(6px);
  transition: background-color 0.1s ease;

  &--dragover {
    background-color: rgba(255, 255, 255, 0.8);
  }

  &__icon {
    margin-bottom: 0.6rem;
    pointer-events: none;

    :deep(svg) {
      fill: rgba($c-dark, 1);
    }

    &--small,
    &--small :deep(svg) {
      width: 3.2rem;
      height: 3rem;
    }

    &--medium,
    &--medium :deep(svg) {
      width: 5.5rem;
      height: 5.1rem;
    }

    &--large,
    &--large :deep(svg) {
      width: 8rem;
      height: 7.4rem;
    }
  }

  &__text {
    max-width: 50%;
    text-align: center;
    pointer-events: none;
    color: $c-dark;
    font-family: 'Montserrat', sans-serif;

    &--small {
      max-width: 60%;
      font-size: 1.4rem;
    }

    &--medium {
      font-size: 1.6rem;
    }

    &--large {
      font-size: 2rem;
    }
  }
}
</style>
